import { useEffect, useState } from 'react';
import dayjs from 'dayjs';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faDiceFive } from '@fortawesome/free-solid-svg-icons';
import forecastGameService, {
  MAX_PINNED_BADGES,
  RecapDiff,
  ResolvedForecast,
} from '@/modules/calendar/services/forecast-game-service';
import { ForecastGameState } from '@/modules/calendar/model/forecast-game/forecast-game-state';
import {
  computeSpecialists,
  MIN_RESOLVED,
  MIN_WIN_RATE,
} from '@/modules/calendar/model/forecast-game/specialists';
import { BADGES } from '@/modules/calendar/model/forecast-game/badges';
import { useOpenWidget } from '@/core/widget-navigation/export';
import ConfettiBurst from '@/modules/calendar/components/forecast-game/confetti-burst';
import { Loading } from '@/shared/components/export';
import styles from '@/modules/calendar/components/forecast-game/forecast-frenzy.module.scss';

const OUTCOME_COPY: Record<ResolvedForecast['outcome'], string> = {
  win: 'Won! 🎉',
  loss: 'Lost — boo 👎',
  tie: 'Tied MNI',
  'no-contest': 'No MNI to beat',
};

// Shown when nothing resolved since last visit — half encouragement, half heckling.
const NOTHING_HAPPENED_LINES = [
  'Nothing happened. The market held its breath just for you — now go do something about it.',
  'Dead quiet. MNI is not scared of you yet.',
  "No results to show. That streak isn't going to build itself.",
  'Zzz... radio silence. Go place a bold call on HSC.',
  "The scoreboard hasn't moved an inch. Neither has your courage, apparently.",
  'Nothing to report — the machine is currently undefeated by default.',
];

function pickNothingHappenedLine(): string {
  return NOTHING_HAPPENED_LINES[Math.floor(Math.random() * NOTHING_HAPPENED_LINES.length)];
}

export default function ForecastFrenzyOverview() {
  const [state, setState] = useState<ForecastGameState>();
  const [recap, setRecap] = useState<RecapDiff | null>(null);
  const [justResolved, setJustResolved] = useState<ResolvedForecast[]>([]);
  // Picked once when nothing resolved, so it doesn't reshuffle on every re-render.
  const [nothingHappenedLine, setNothingHappenedLine] = useState<string>('');
  const openWidget = useOpenWidget();

  useEffect(() => {
    forecastGameService.checkIn().then(result => {
      setState(result.state);
      setRecap(result.recap);
      setJustResolved(result.justResolved);
      if (result.justResolved.length === 0) setNothingHappenedLine(pickNothingHappenedLine());
    });
  }, []);

  if (!state) return <Loading />;

  const { stats, pending } = state;
  const specialists = computeSpecialists(stats.byIndicator);
  // currentStreak is signed internally (negative = active losing streak, used
  // by the Comeback Kid badge) — the scoreboard only ever shows the win streak.
  const winStreak = Math.max(stats.currentStreak, 0);

  // `stats.badges` is append-only, so the last entry is the most recently
  // earned badge ever — not just one gained during this visit's recap.
  const latestBadgeId = stats.badges[stats.badges.length - 1];
  const latestBadge = latestBadgeId ? BADGES.find(b => b.id === latestBadgeId) : undefined;

  const pinnedBadgeDefs = (state.pinnedBadges ?? [])
    .map(id => BADGES.find(b => b.id === id))
    .filter((b): b is (typeof BADGES)[number] => b !== undefined);

  const goToHsc = () => {
    openWidget('/calendar', '/daily/today');
  };

  return (
    <div>
      <div className={'d-flex flex-column gap-3'}>
        <div className={styles.scoreboard}>
          <div className={styles.scoreboardScore}>
            You {stats.wins} — {stats.losses} MNI
          </div>
          {stats.ties > 0 && (
            <div className={`${styles.scoreboardTies} text-subtitle`}>{stats.ties} ties</div>
          )}

          <div className={styles.statRow}>
            <div>
              <div className={styles.statNumberSlot}>
                <div
                  className={`${styles.jackpotDigit} ${winStreak >= 3 ? styles.jackpotDigitHot : ''}`}
                >
                  {winStreak}
                </div>
              </div>
              <div className={`${styles.statLabel} text-small`}>Streak</div>
            </div>
            <div>
              <div className={styles.statNumberSlot}>
                <div className={styles.statValue}>{stats.bestStreak}</div>
              </div>
              <div className={`${styles.statLabel} text-small`}>Best</div>
            </div>
            <div>
              <div className={styles.statNumberSlot}>
                <div className={styles.statValue}>{pending.length}</div>
              </div>
              <div className={`${styles.statLabel} text-small`}>Upcoming</div>
            </div>
          </div>
        </div>

        <button
          type={'button'}
          className={`btn ${styles.casinoButton} align-self-center`}
          onClick={goToHsc}
        >
          <FontAwesomeIcon icon={faDiceFive} className={styles.casinoDice} />
          Place a forecast on HSC
        </button>

        {recap && recap.newBadges.length > 0 && <ConfettiBurst />}

        <div className={`${styles.recapBanner} text-content`}>
          <div className={`fw-semibold mb-2 ${styles.recapTitle} text-content`}>
            While you were away
          </div>

          {recap && recap.newBadges.length > 0 && (
            <div className={'mb-2'}>
              New achievement{recap.newBadges.length > 1 ? 's' : ''}:{' '}
              {recap.newBadges.map(badge => `${badge.emoji} ${badge.label}`).join(', ')}
            </div>
          )}

          {justResolved.length === 0 && (
            <div className={styles.banterText}>{nothingHappenedLine}</div>
          )}

          {justResolved.length > 0 && (
            <div className={`d-flex flex-column gap-2 ${styles.revealRows}`}>
              {justResolved.map((item, index) => (
                <div
                  key={`${item.seriesDisplay}-${item.date}`}
                  className={`${styles.pendingRow} text-content ${styles.revealRow} d-flex justify-content-between align-items-center`}
                  style={{ animationDelay: `${index * 120}ms` }}
                >
                  <div>
                    <div className={'fw-semibold'}>{item.seriesDisplay}</div>
                    <div className={`${styles.pendingDate} text-small`}>
                      {dayjs(item.date).format('DD MMM')} — actual: {item.actual}
                    </div>
                    <div className={'mt-1'}>
                      <span className={`${styles.valuePill} ${styles.valuePillYou}`}>
                        You: {item.userValue}
                      </span>
                      <span className={`${styles.valuePill} ${styles.valuePillMni}`}>
                        MNI: {item.mniValue ?? '—'}
                      </span>
                    </div>
                  </div>
                  <div className={'fw-semibold'}>{OUTCOME_COPY[item.outcome]}</div>
                </div>
              ))}
            </div>
          )}
        </div>

        <div className={`${styles.recapBanner} text-content`}>
          <div className={`fw-semibold mb-2 ${styles.recapTitle} text-content`}>Specialties</div>
          {specialists.length > 0 ? (
            <div className={'d-flex flex-wrap gap-2'}>
              {specialists.map(s => (
                <span key={s.dataSeriesId} className={`${styles.valuePill} ${styles.valuePillYou}`}>
                  {s.display} · {Math.round(s.winRate * 100)}%
                </span>
              ))}
            </div>
          ) : (
            <div className={styles.banterText}>
              None yet — resolve at least {MIN_RESOLVED} forecasts on the same indicator with a{' '}
              {Math.round(MIN_WIN_RATE * 100)}%+ win rate to become a specialist in it.
            </div>
          )}
        </div>

        <div className={`${styles.recapBanner} text-content`}>
          <div className={`fw-semibold mb-2 ${styles.recapTitle} text-content`}>Achievements</div>
          <div className={'d-flex flex-wrap align-items-center gap-2'}>
            {latestBadge && (
              <span
                className={`${styles.achievementChip} ${styles.achievementChipLatest}`}
                title={`Latest: ${latestBadge.description}`}
              >
                {latestBadge.emoji} {latestBadge.label}
              </span>
            )}
            {pinnedBadgeDefs.map(badge => (
              <span key={badge.id} className={styles.achievementChip} title={badge.description}>
                {badge.emoji} {badge.label}
              </span>
            ))}
            {!latestBadge && pinnedBadgeDefs.length === 0 && (
              <span className={styles.banterText}>
                Nothing yet — earn a badge, then pin your favorites (up to {MAX_PINNED_BADGES}) in
                the Badges tab.
              </span>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
